import React, { useEffect, useMemo, useState } from 'react'

import { Box, Button, Flex, Heading, Spacer } from '@chakra-ui/react'

import { TooltipCell } from '@/components/Table/components/Cell/ToolTipCell'
import ApplicationDetailModal from '../components/ApplicationDetailModal'

import { useInitialTableState } from '@/components/Table/components/useInitialTableState'
import FullTable from '@/components/Table/FullTable/FullTable'
import { convertTableStateToQuery } from '@/utils/tableHelper'
import { createColumnHelper } from '@tanstack/react-table'

import DateCell from '@/components/Table/components/Cell/DateCell'
import { useApplicationType } from '@/features/applicationTypes/useApplicationType'
import { useApplication } from '../useApplication'
import useSystemVariable from '@/features/systemVariables/useSystemVariable'
import { SystemVariableType } from '@/features/systemVariables/constants'
import { FilterType } from '@/components/Table/components/Filter/TableFilter'

const columnHelper = createColumnHelper()

const ApplicationListPage = () => {
  // Local State
  const [storedTableState, setStoredTableState] = useInitialTableState(`table_state:application`, {})
  const query = convertTableStateToQuery(storedTableState)

  const [modalDetail, setModalDetail] = useState({
    show: false,
    detail: {},
  })

  const { useFetchAllApplications } = useApplication()
  const { useFetchAllApplicationTypes, useApplicationTypeOptions } = useApplicationType()
  const { useSystemVariableOptions } = useSystemVariable()

  const { data: applicationResponse } = useFetchAllApplications(query)

  const { data: applicationTypeResponse } = useFetchAllApplicationTypes({ page: 1, pageSize: 0 })

  const { data: applicationCategoryOptions } = useSystemVariableOptions(SystemVariableType.APPLICATION_CATEGORY, {
    status: 'active',
  })

  const { data: applicationTypeOptions } = useApplicationTypeOptions({ status: 'active' })
  const handleCreateApplication = () => {
    setModalDetail({
      show: true,
      isCreate: true,
      detail: {},
    })
  }

  const handleRowClick = (row) => {
    let detail = row
    if (row.firstApproverType === 'lineManager' && row?.firstApproverId) {
      detail = {
        ...row,
        lineManager: row.firstApproverId,
      }
    }

    setModalDetail({
      show: true,
      isCreate: false,
      detail: detail,
    })
  }

  const defaultColumns = useMemo(
    () => [
      columnHelper.accessor('categoryName', {
        header: () => <span>Category</span>,
        meta: {
          filterVariant: FilterType.SELECT,
          filterOptions: applicationCategoryOptions,
        },
      }),
      columnHelper.accessor('typeName', {
        header: () => <span>Application Type</span>,
        meta: {
          filterVariant: FilterType.SELECT,
          filterOptions: applicationTypeOptions,
        },
      }),
      columnHelper.accessor('validity'),
      columnHelper.accessor('reference', {
        header: () => <span>Reference Should be Probided by Applicant</span>,
      }),

      columnHelper.accessor('nameOfHolder'),
      columnHelper.accessor('firstApproverName', {
        header: () => <span>First Approver</span>,
        cell: ({ row: { original }, getValue }) => {
          const value = getValue()
          if (!value || value === null || value === '') {
            return <>Not Required</>
          }

          return <TooltipCell text={`${value} (${original?.firstApproverStatus || ''})`} />
        },
        meta: {
          filterVariant: 'select ',
          filterOptions: [
            {
              value: 'required',
              label: 'required',
            },
            {
              value: 'upcoming',
              label: 'Upcoming',
            },
            {
              value: 'approved',
              label: 'approved',
            },
            {
              value: 'rejected',
              label: 'rejected',
            },
          ],
        },
      }),
      columnHelper.accessor('secondApproverName', {
        header: () => <span>Second Approver</span>,
        cell: ({ row: { original }, getValue }) => {
          const value = getValue()
          if (!value || value === null || value === '') {
            return <>Not Required</>
          }

          return <TooltipCell text={`${value} (${original?.secondApproverStatus || ''})`} />
        },
        meta: {
          filterVariant: 'select ',
          filterOptions: [
            {
              value: 'required',
              label: 'required',
            },
            {
              value: 'upcoming',
              label: 'Upcoming',
            },
            {
              value: 'approved',
              label: 'approved',
            },
            {
              value: 'rejected',
              label: 'rejected',
            },
          ],
        },
      }),
      columnHelper.accessor('finalApproverName', {
        header: () => <span>Final Approver</span>,
        cell: ({ row: { original }, getValue }) => {
          const value = getValue()
          if (!value || value === null || value === '') {
            return <>Not Required</>
          }

          return <TooltipCell text={`${value} (${original?.finalApproverStatus || ''})`} />
        },
        meta: {
          filterVariant: 'select ',
          filterOptions: [
            {
              value: 'required',
              label: 'required',
            },
            {
              value: 'upcoming',
              label: 'Upcoming',
            },
            {
              value: 'approved',
              label: 'approved',
            },
            {
              value: 'rejected',
              label: 'rejected',
            },
          ],
        },
      }),
      columnHelper.accessor('createdAt', {
        header: () => <span>Applied Date</span>,
        cell: DateCell,
        enableColumnFilter: false,
      }),
      columnHelper.accessor('status'),
    ],
    []
  )

  return (
    <>
      <ApplicationDetailModal modalDetail={modalDetail} setModalDetail={setModalDetail} />
      <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
        <Flex p={5} borderBottom="1px solid #d8dbe0">
          <Heading> Applications </Heading>
          <Spacer />
          <Button colorScheme="green" onClick={handleCreateApplication}>
            Apply
          </Button>
        </Flex>
        <Box p={5} overflowX="scroll">
          <FullTable
            data={applicationResponse?.data ? applicationResponse.data : []}
            rowCount={applicationResponse?.count}
            columnDef={defaultColumns}
            state={storedTableState}
            setState={setStoredTableState}
            handleRowClick={handleRowClick}
          />
        </Box>
      </Box>
    </>
  )
}

export default ApplicationListPage
